import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { ESolutionNames } from '@/types/integrations.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Modal from '@ui/modal/modal';
import OptionalComponent from '@ui/optional-component/optional-component';
import Switch from '@ui/switch/switch';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { ChangeEvent, useState } from 'react';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { getIntegrationResponse, getIntegrationSchema, isExternalRegistration } from '../../../utils';
import { getWorkingProject } from '@/store/slices/project/selectors';
import Text from '@ui/text/text/text';
import TextCopyBox from '../text-copy-box/text-copy-box.tsx';
import { validateUrl } from '@/utils/validate-url.ts';
import { saveWorkingProjectByFields } from '@/store/slices/project/index.ts';
import Button from '@ui/buttons/button/button.tsx';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable.ts';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable.ts';
import { EColorVariablesV2 } from '@/types/theme-packs.types.ts';

interface InboundIntegrationModalProps {
  solution: string;
  handleCloseModal: () => void;
}

export default function InboundIntegrationModal({ solution, handleCloseModal }: InboundIntegrationModalProps) {
  const workingEvent = useAppSelector(getWorkingProject);

  const [isSaving, setIsSaving] = useState(false);
  const [closedNativeRegistration, setClosedNativeRegistration] = useState(isExternalRegistration(workingEvent));
  const [externalRegLink, setExternalRegLink] = useState(
    workingEvent?.registration_settings?.externalRegistration?.url || ''
  );

  const dispatch = useAppDispatch();

  const constructIboundRegistrationUrl = () => {
    const host = import.meta.env.VITE_HV_HOST;
    if (!solution || !host) return '';
    if (solution === ESolutionNames.Custom) {
      return `${host}/inbound-registration`;
    }
    return `${host}/inbound-registration/${solution?.toLowerCase()}`;
  };

  const handleExternalUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setExternalRegLink(e.target.value);
  };

  const invalidUrl = !validateUrl(externalRegLink);

  const handleSaveModal = async () => {
    if (!workingEvent?.registration_settings) return;
    setIsSaving(true);

    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...workingEvent?.registration_settings,
          externalRegistration: {
            isOn: closedNativeRegistration,
            url: externalRegLink,
          },
        },
        saveId: 'update-navtive-registration',
      })
    );

    handleCloseModal();
  };

  const handleCloseRegistration = async (_value: string, _isClosed: boolean) => {
    setClosedNativeRegistration(_isClosed);
  };

  return (
    <Modal
      customSize={{
        width: '742px',
        height: 'auto',
        maxWidth: '742px',
        maxHeight: '90%',
        minHeight: 'auto',
      }}
      open={true}
      contentWrapperClassName="padding-v-24 padding-h-40"
      onRequestClose={handleCloseModal}
      RenderHeaderContent={() => (
        <Text tag="h3" className="H3 margin-b-20">
          {solution}
        </Text>
      )}
      RenderFooterContent={() => (
        <div className="flex-row jc-flex-end ai-center gap-8 margin-t-16">
          <button disabled={isSaving} onClick={handleCloseModal}>
            <Text>{isSaving ? <WaitingIndicator /> : 'Close'}</Text>
          </button>
          <Button
            onClick={handleSaveModal}
            disabled={isSaving || (invalidUrl && closedNativeRegistration)}
            loading={isSaving}
            backgroundColor={getThemeColorCSSVariable()}
            className="flex ai-center jc-center"
            hoverOpacity={0.8}
          >
            <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}>
              Save
            </Text>
          </Button>
        </div>
      )}
    >
      {workingEvent ? (
        <div className="inbound-payload-format-modal">
          <TextCopyBox label="API endpoint" textToCopy={constructIboundRegistrationUrl()} />
          <TextCopyBox label="Event identifier" textToCopy={workingEvent.uuid} />
          <TextCopyBox label="Payload format" textToCopy={getIntegrationSchema(solution, workingEvent?.uuid) || ''} />
          <OptionalComponent display={!!solution}>
            <TextCopyBox label="Example response" textToCopy={getIntegrationResponse(solution) || ''} />
          </OptionalComponent>
          <div className="flex fd-row ai-center gap-8 margin-t-16">
            <Text tag="label">Disable Native Registrations</Text>
            <Switch
              on={closedNativeRegistration}
              onClick={handleCloseRegistration}
              value={'Disable Native Registrations'}
            />
            <TooltipPortal
              tooltipContent={
                <Text color="var(--gray-true-dark-100)">Publish the event from the editor to save any changes.</Text>
              }
            >
              <Icon name={ICONS.PRIMARY_TOOLTIP} size={12} color={'var(--primary-300)'} />
            </TooltipPortal>
          </div>
          {closedNativeRegistration && (
            <InputWithOverlay
              inputProps={{
                placeholder: 'Registration link',
                onChange: handleExternalUrlChange,
                value: externalRegLink,
              }}
              wrapperClass="margin-t-16"
              status={invalidUrl ? 'error' : undefined}
            />
          )}
        </div>
      ) : (
        <WaitingIndicator />
      )}
    </Modal>
  );
}
